import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, X, Share, Plus, Smartphone } from "lucide-react";

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = localStorage.getItem("dietflix-install-dismissed") === "1";

    const onBip = (e) => { e.preventDefault(); setDeferred(e); if (!dismissed) setShow(true); };
    const onInstalled = () => { setShow(false); setDeferred(null); };
    const onRequest = () => doInstall();

    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("dietflix:install-request", onRequest);

    if (isIos() && !dismissed) setShow(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("dietflix:install-request", onRequest);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doInstall = async () => {
    if (isStandalone()) return;
    if (isIos()) { setIosOpen(true); return; }
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch {}
      setDeferred(null);
      setShow(false);
    } else {
      // Installability not yet available (e.g. desktop/unsupported) — show iOS-style hint as generic guidance
      setIosOpen(true);
    }
  };

  const dismiss = () => { setShow(false); localStorage.setItem("dietflix-install-dismissed", "1"); };

  return (
    <>
      {show && (
        <div className="fixed inset-x-0 bottom-16 sm:bottom-0 z-[55] p-3 sm:p-4 pointer-events-none" data-testid="install-banner">
          <div className="pointer-events-auto mx-auto max-w-md rounded-2xl border bg-card shadow-2xl p-3 flex items-center gap-3">
            <img src="/icon-192.png" alt="DietFlix" className="h-11 w-11 rounded-xl shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Installa DietFlix</div>
              <p className="text-xs text-muted-foreground">Aggiungila alla schermata home per usarla come un'app.</p>
            </div>
            <Button size="sm" onClick={doInstall} data-testid="install-button" className="shrink-0">
              <Download className="h-4 w-4 mr-1.5" /> Installa
            </Button>
            <button onClick={dismiss} className="text-muted-foreground hover:text-foreground shrink-0" data-testid="install-dismiss" aria-label="Chiudi">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent data-testid="ios-install-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> Installa DietFlix</DialogTitle>
            <DialogDescription>Aggiungi l'app alla schermata Home in pochi secondi.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex items-center gap-3">
              <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold shrink-0">1</span>
              <span className="flex items-center gap-1.5">Tocca l'icona <Share className="h-4 w-4 text-primary" /> <b>Condividi</b> nella barra del browser (Safari).</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold shrink-0">2</span>
              <span className="flex items-center gap-1.5">Scegli <Plus className="h-4 w-4 text-primary" /> <b>Aggiungi a Home</b>.</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold shrink-0">3</span>
              <span>Conferma con <b>Aggiungi</b>: troverai DietFlix tra le tue app.</span>
            </li>
          </ol>
          <p className="text-xs text-muted-foreground">Su Android: apri il menu ⋮ del browser e scegli "Installa app" / "Aggiungi a schermata Home".</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
